import { useQuery } from '@tanstack/react-query';
import { Building2, LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { CategoryItem } from '~/.server/api/routers/workspace';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '~/components/ui/card';
import { AmenityDisplayInfo, amenityIcons, AmenityKey } from '~/utils/workspace/amenities';

interface AmenityListProps {
  amenities: string[];
  maxVisible?: number;
  showAll?: boolean;
  onShowAll?: () => void;
  className?: string;
}

interface AmenityItemProps {
  icon: LucideIcon;
  label: string;
}

const AmenityItem = ({ icon: Icon, label }: AmenityItemProps) => (
  <div className="flex items-center gap-2">
    <Icon className="h-5 w-5 text-muted-foreground" />
    <span className="capitalize">{label}</span>
  </div>
);

export const AmenityList = ({ 
  amenities, 
  maxVisible = 6,
  className = ''
}: AmenityListProps) => {
  const { data: amenityData } = useQuery<CategoryItem[]>({ 
    queryKey: ['amenities'],
  });
  
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const visibleAmenities = showAllAmenities ? amenities : amenities.slice(0, maxVisible);
  const hasMore = amenities.length > maxVisible;

  const getAmenityInfo = (amenity: string): AmenityDisplayInfo => {
    const amenityItem = amenityData?.find(item => item.value === amenity);
    return {
      icon: amenityIcons[amenity as AmenityKey] || Building2,
      label: amenityItem?.label || amenity // Fallback to the amenity key if label not found
    };
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Este espacio cuenta con</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visibleAmenities.map((amenity) => {
            const { icon, label } = getAmenityInfo(amenity);
            return <AmenityItem key={amenity} icon={icon} label={label} />;
          })}
        </div>
      </CardContent>
      {hasMore && !showAllAmenities && (
        <CardFooter>
          <Button variant="outline" onClick={() => setShowAllAmenities(true)}>
            Ver todas
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};
